import { get, includes, isEmpty, omitBy, isNil } from 'lodash';
import { capitalizeString, translateMake } from '../../../utils/commonHelper';
import { getActiveParams, getDefaultParams, generateSearchPath, parseSearchAppParams, hyphenateUrlComponents, unhyphenateUrlComponents } from '../../../utils/urlHelpers/boats';
import { getPartyBrandedSRP } from '../../../utils/urlHelpers/party';
import { capitalizeEachWord, formatNumber } from '@dmm/lib-common/lib/formatting';
import messages from '../../../utils/translationMessages';
import { formatMessage as t, getCurrentBoats as bts, getCurrentLocale } from '../../../utils/language';
import { isSingleFaceted } from '../../../utils/multiFacetHelper';
import { urlFromLanguage } from '../../../utils/seo';
import { getPortalDefaultCountry } from '../../../utils/locationHelper';

export const getMetaTitle = (
  params,
  maxPages,
  portal,
  isBranded = false,
  partyDetails = {},
  countListings,
  locale) => {
  let paginationTranslation, titleTranslation;
  const listings = countListings >= 10 ? formatNumber(countListings, locale) : '';
  const displayListings = listings ? `${listings} ` : '';

  if (isBranded) {
    const translationParameters = {
      dealerName: partyDetails.name,
      dealerCity: get(partyDetails, 'address.city', ''),
      portal,
    };
    titleTranslation = `${t(messages.brandedSearch.SEO.metaElements.title,
      { ...translationParameters } )}`;
    paginationTranslation = getPaginationTranslation(params, maxPages, 'title');
    return paginationTranslation ?
      `${titleTranslation} - ${paginationTranslation}` : `${titleTranslation}`;
  }
  titleTranslation = `${getSEOElementTranslation(params, portal, 'title')} | ${portal}`;
  paginationTranslation = getPaginationTranslation(params, maxPages, 'title');

  return paginationTranslation ?
    `${displayListings}${titleTranslation} | ${paginationTranslation}` : `${displayListings}${titleTranslation}`;
};

export const getMetaDescription =
  (params, maxPages, portal, isBranded = false, partyDetails = {}) => {
    let descriptionTranslation, paginationTranslation;
    if (isBranded) {
      const translationParameters = {
        dealerName: partyDetails.name,
        dealerCity: get(partyDetails, 'address.city', ''),
        portal,
      };
      descriptionTranslation = t(messages.brandedSearch.SEO.metaElements.description,
        { ...translationParameters } );
      paginationTranslation = getPaginationTranslation(params, maxPages, 'description');
      return paginationTranslation ? `${paginationTranslation} | ${descriptionTranslation}` : descriptionTranslation;
    }
    descriptionTranslation = getSEOElementTranslation(params, portal, 'description');
    paginationTranslation = getPaginationTranslation(params, maxPages, 'description');
    return paginationTranslation ? `${descriptionTranslation} ${paginationTranslation}` : descriptionTranslation;
  };

export const getH1 = (params, portal, isBranded = false, partyDetails = {}, countListings, locale) => {
  let h1Translation;
  const listings = countListings >= 10 ? formatNumber(countListings, locale) : '';
  const displayListings = listings ? `${listings} ` : '';

  if (isBranded) {
    h1Translation = partyDetails.displayName;
    return h1Translation;
  }
  h1Translation = getSEOElementTranslation(params, portal, 'title');
  return `${displayListings}${h1Translation}`;
};

export const getNoFollow = (url, isBranded = false) => {
  let noFollow = true;
  const activeParams = getActiveParams(parseSearchAppParams(url, isBranded));
  Object.entries(activeParams).forEach(([paramKey, paramValue]) => {
    if (includes(['modal', 'modelRange'] ,paramKey)) {
      noFollow = false;
    }
    if (includes(['hullMaterial', 'fuelType'], paramKey)) {
      noFollow = !isSingleFaceted(paramValue);
    }
  });
  return noFollow;
};

export const getNoIndex = (url, isBranded = false) => {
  const activeParams = getActiveParams(parseSearchAppParams(url, isBranded));

  return Object.entries(activeParams).some(([paramKey, paramValue]) => {
    if (includes(['keyword'] ,paramKey)) {
      return true;
    }

    if (!includes(isBranded ? bts().BRANDED_INDEXABLE_FACETS : bts().INDEXABLE_FACETS, paramKey)) {
      return true;
    }

    if (includes(['multiFacetedBoatTypeClass', 'makeModel', 'city', 'hullMaterial', 'fuelType'], paramKey)) {
      return (isBranded && !isEmpty(paramValue)) || !isSingleFaceted(paramValue);
    }

    return false;
  });
};

export const getLinkRelPrev = () => {
  return '';
};

export const getLinkRelNext = () => {
  return '';
};

export const getCanonical = (params, isBranded = false, partyDetails = {}) => {
  let canonicalUrlParams = {};
  const defaultParams = getDefaultParams(params);
  const activeParams = getActiveParams(defaultParams);
  const activeCanonicalParamsCount = Object.keys(activeParams)
    .filter(key => !bts().NON_CANONICAL_PARAMS.includes(key)).length;
  const locale = getCurrentLocale();

  if (isBranded) {
    return urlFromLanguage(getPartyBrandedSRP(partyDetails), locale);
  }

  if (Object.keys(params).some(key => !bts().INDEXABLE_PARAMS.includes(key) && !isEmpty(params[key]))) {
    return '';
  }

  // Location params will only have either region + subdivision[*] or country + city[*].
  // [*] Optional.
  let locationParams = {
    region: activeParams.region,
    subdivision: activeParams.subdivision,
    country: activeParams.country,
    city: activeParams.city
  };
  locationParams = omitBy(locationParams, isNil);

  if (activeParams.makeModel) { // Make + Model
    canonicalUrlParams = { makeModel: activeParams.makeModel };
  }

  if (!isEmpty(locationParams)) { // Location
    canonicalUrlParams = { ...canonicalUrlParams, ...locationParams };
  }

  if (activeParams.condition && isEmpty(canonicalUrlParams)) { // condition
    canonicalUrlParams = { condition: activeParams.condition };
  }

  if (activeParams.multiFacetedBoatTypeClass && !isEmpty(activeParams.multiFacetedBoatTypeClass)) { // Type + Class
    if (activeCanonicalParamsCount === 1) {
      canonicalUrlParams = { multiFacetedBoatTypeClass: activeParams.multiFacetedBoatTypeClass };
    }

    if (activeCanonicalParamsCount === 2) {
      if (!isEmpty(locationParams)) { // type + class + region
        canonicalUrlParams = { multiFacetedBoatTypeClass: activeParams.multiFacetedBoatTypeClass, ...locationParams };
      }
      if (!Object.keys(activeParams.multiFacetedBoatTypeClass)
        .some(key => !activeParams.multiFacetedBoatTypeClass[key][0].includes('all'))) { // only Type

        if (activeParams.condition) { //type + condition
          canonicalUrlParams = {
            multiFacetedBoatTypeClass: activeParams.multiFacetedBoatTypeClass,
            condition: activeParams.condition
          };
        }
        if (activeParams.makeModel
          && !Object.keys(activeParams.makeModel).some(key => !isEmpty(activeParams.makeModel[key]))) { // type + make
          canonicalUrlParams = {
            multiFacetedBoatTypeClass: activeParams.multiFacetedBoatTypeClass,
            makeModel: activeParams.makeModel
          };
        }
      }
    }
    if (Object.keys(locationParams).length === 2 && (activeCanonicalParamsCount === 3)) { // type + class + region + subdivision
      canonicalUrlParams = { multiFacetedBoatTypeClass: activeParams.multiFacetedBoatTypeClass, ...locationParams };
    }
  }
  if (activeParams.page) {
    canonicalUrlParams = { ...canonicalUrlParams, page: activeParams.page };
  }

  if (activeParams.owner) {
    canonicalUrlParams = { ...canonicalUrlParams, owner: activeParams.owner };
  }

  if (activeParams.modelRange) {
    canonicalUrlParams = { ...canonicalUrlParams, modelRange: activeParams.modelRange };
  }

  if (activeParams.hullMaterial && activeParams.fuelType &&
      activeCanonicalParamsCount === Object.keys(locationParams).length + 3) {
    canonicalUrlParams = {
      fuelType: activeParams.fuelType,
      ...locationParams
    };
  } else if ((activeParams.hullMaterial || activeParams.fuelType) && activeParams.makeModel &&
      activeCanonicalParamsCount >= Object.keys(locationParams).length + 3) {
    canonicalUrlParams = {
      makeModel: activeParams.makeModel,
      multiFacetedBoatTypeClass: { [Object.keys(activeParams.multiFacetedBoatTypeClass)[0]]: [] },
      ...locationParams
    };
  } else if ((activeParams.hullMaterial || activeParams.fuelType) &&
      activeCanonicalParamsCount === Object.keys(locationParams).length + 2) {
    if (activeParams.multiFacetedBoatTypeClass && isEmpty(activeParams.multiFacetedBoatTypeClass)) { // Location (optional) + hull only
      canonicalUrlParams = {
        hullMaterial: activeParams.hullMaterial,
        fuelType: activeParams.fuelType,
        ...locationParams
      };
    }
    if (activeParams.multiFacetedBoatTypeClass && !isEmpty(activeParams.multiFacetedBoatTypeClass)) { // Type + class + hull only
      if (!isEmpty(locationParams)) {
        canonicalUrlParams = {
          multiFacetedBoatTypeClass: activeParams.multiFacetedBoatTypeClass,
          ...locationParams
        };
      } else {
        canonicalUrlParams = {
          hullMaterial: activeParams.hullMaterial,
          fuelType: activeParams.fuelType,
          multiFacetedBoatTypeClass: activeParams.multiFacetedBoatTypeClass
        };
      }
    }
  } else if ((activeParams.hullMaterial || activeParams.fuelType) && activeParams.condition &&
      activeCanonicalParamsCount === Object.keys(locationParams).length + 3 &&
      !Object.keys(activeParams.multiFacetedBoatTypeClass)
        .some(key => !activeParams.multiFacetedBoatTypeClass[key][0].includes('all'))) {
    canonicalUrlParams = {
      condition: activeParams.condition,
      multiFacetedBoatTypeClass: activeParams.multiFacetedBoatTypeClass,
      ...locationParams
    };
  }

  return urlFromLanguage(generateSearchPath(canonicalUrlParams, getDefaultParams({}), false), locale);
};

export const getSEOParams = (params, _makeModel) => {
  let {
    condition,
    makeModel = {},
    modelRange,
    multiFacetedBoatTypeClass,
    region,
    subdivision,
    page,
    forSale,
    country,
    city,
    fuelType,
    hullMaterial
  } = getActiveParams(getDefaultParams(params));
  let type = get(Object.keys(multiFacetedBoatTypeClass), '0', '');
  let boatClass = get(multiFacetedBoatTypeClass, `${type}.0`, '');
  let _make = get(Object.keys(makeModel), '0', '');
  const _model = get(makeModel, `${_make}.0`, '');
  const selectedMakeModelFacet = _makeModel.find(e =>
    hyphenateUrlComponents(e.value) === _make
  );
  let makeObj = selectedMakeModelFacet ? selectedMakeModelFacet : { value: _make };
  const modelObj = unhyphenateUrlComponents(_model);
  const make = get(makeObj, 'value', '');
  const model = capitalizeEachWord(modelObj);
  modelRange = modelRange && capitalizeEachWord( unhyphenateUrlComponents(modelRange) );

  return {
    boatClass,
    condition,
    make,
    model,
    modelRange,
    region,
    subdivision,
    page,
    type,
    sellerType: forSale,
    country,
    city,
    fuelType,
    hullMaterial
  };

};

export const getSEOElementTranslation = (params, portal, element) => {
  // Get all indexable facets values of the current search translated
  let {
    boatClass,
    condition,
    make,
    model,
    modelRange,
    region,
    subdivision,
    type,
    sellerType,
    country,
    city,
    fuelType,
    hullMaterial
  } = params;
  make = translateMake(make);

  const getFacetTranslation = (messagePath) => {
    const message = get(messages, messagePath);
    if (message) {
      return t(message);
    }
    return '';
  };

  const subdivisionCountry = country || getPortalDefaultCountry();

  boatClass = bts().allTypes.includes(boatClass) ? undefined : boatClass;
  condition = condition && getFacetTranslation(`searchPageSEO.indexableFacets.condition[${condition}]`).toLowerCase();
  type = type && getFacetTranslation(type).toLowerCase();
  boatClass = boatClass && getFacetTranslation(`classFacetValues[${boatClass}]`).toLowerCase();
  region = region && getFacetTranslation(`countryRegions[${region}]`);
  subdivision = subdivision && getFacetTranslation(`countrySubdivision.${subdivisionCountry}.${subdivision}`);
  sellerType = sellerType && getFacetTranslation(`forSaleFacet[${sellerType}]`);
  country = country && getFacetTranslation(`countries[${country}]`);
  city = city && city.length > 0 && capitalizeString(city[0]);
  fuelType = fuelType && (getFacetTranslation(`fuelTypeHeadingText[${fuelType[0]}]`) ?
    getFacetTranslation(`fuelTypeHeadingText[${fuelType[0]}]`) :
    getFacetTranslation(`fuelTypeFacetText[${fuelType[0]}]`));
  hullMaterial = hullMaterial && getFacetTranslation(`hullMaterialFacetText[${hullMaterial[0]}]`);

  const locationFacet = {};
  locationFacet[city ? 'city' : subdivision ? 'subdivision' : region ? 'region' : 'country'] = city || subdivision || region || country;
  const indexableFacets = {
    class: boatClass,
    condition,
    make,
    model,
    modelRange,
    type,
    sellerType,
    ...locationFacet,
    fuelType,
    hullMaterial
  };

  let messageKey = 'searchPageSEO.metaElements';
  bts().INDEXABLE_TRANSLATION_PARAMS_PRIORITY.forEach((facet) => {
    if (indexableFacets[facet] && get(messages, `${messageKey}.${facet}`)) {
      messageKey += `.${facet}`;
    }
  });

  // If the resulting key doesn't have a template, remove the last part until getting a valid combination of facets
  while (!get(messages, `${messageKey}.${element}`)) {
    if (messageKey.includes('.')) {
      messageKey = messageKey.slice(0, messageKey.lastIndexOf('.'));
    } else {
      // Couldn't find a message for that element and combination of facets
      return undefined;
    }
  }

  // Inject the parameters to the template and return the capitalized translation
  const translation = t(get(messages, `${messageKey}.${element}`), { ...indexableFacets, portal });
  return capitalizeString(translation);
};

export const getPaginationTranslation = (params, maxPages, element) => {
  const { page } = params;
  if (page && page !== '1') {
    const message = messages.searchPageSEO.pagination[element];
    return message ? t(messages.searchPageSEO.pagination[element], { page, totalPages: maxPages }) : undefined;
  }
  return '';
};
